import React, { useEffect, useRef, useState } from 'react'

import SafetySystemsModal from './SafetySystemsModal'
import SensorInsights, { StatusBadge, displaySensorName, formatPrice } from './adasInsights'

const normalizeServiceDisplayName = (name) => {
  if (!name) return ''
  const rawName = String(name).trim()
  if (!rawName) return ''
  const stripped = rawName
    .replace(/\s*[-–]?\s*\(?(oem|aftermarket)\)?\s*$/i, '')
    .trim()
  return stripped || rawName
}

// Diagnostic scans carry an OEM/Aftermarket choice. normalizeServiceDisplayName
// strips any trailing "(OEM)/(Aftermarket)" from the name, so re-derive the scan
// type — prefer the explicit svc.scanType, else recover it from the raw operation
// name — and re-append it consistently as " (OEM)" / " (Aftermarket)".
const scanTypeSuffix = (svc) => {
  if (!svc) return ''
  let type = svc.scanType
  if (!type && svc.operation && svc.operation.name) {
    const match = String(svc.operation.name).match(/\(?(oem|aftermarket)\)?\s*$/i)
    if (match) type = match[1]
  }
  const normalized = String(type || '').toLowerCase()
  if (normalized === 'oem') return ' (OEM)'
  if (normalized === 'aftermarket') return ' (Aftermarket)'
  return ''
}

const menuStyles = {
  menu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    background: '#fff',
    border: '1px solid #E0E6EB',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    zIndex: 10,
    minWidth: 140,
    overflow: 'hidden',
  },
  item: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    padding: '10px 16px',
    fontSize: 14,
    color: '#2A2A2A',
    cursor: 'pointer',
  },
}

const AdasSections = ({ report, allowViewPricing }) => {
  const [expandAll, setExpandAll] = useState(false)
  const [showSafety, setShowSafety] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef(null)
  const services = (report.adas && report.adas.services) || []

  useEffect(() => {
    if (!showMenu) return undefined
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMenu])
  const sensorInsights = report.sensorInsights || []
  // Use the same collision flag as the admin portal: the editable, top-level
  // report field (admin toggles it via vehicleInvolvedInCollision), NOT the
  // VIN-decode hint under adas.vinDecodeData — the two can diverge.
  const collision = !!report.vehicleInvolvedInCollision
  const make = ((report.vehicle && report.vehicle.make) || '').toLowerCase()
  const collisionPhrase = make === 'ford' ? 'an accident' : 'a collision'

  if (!services.length) {
    return <p style={{ color: '#69787F', marginTop: 24 }}>No ADAS services on this report.</p>
  }

  // Group by categoryName, then by sensorName — matching admin's filter:
  // hide not-equipped sensors, and hide collision-only services when the
  // vehicle was not in a collision.
  const categories = {}
  services.forEach(svc => {
    if (svc.recommendedStatus === 'not-equipped') return
    if (svc.collisionRelated && !collision) return
    const cat = svc.categoryName || 'Uncategorized'
    if (!categories[cat]) categories[cat] = {}
    const sensor = svc.sensorName || 'General'
    if (!categories[cat][sensor]) categories[cat][sensor] = { services: [] }
    categories[cat][sensor].services.push(svc)
  })

  return (
    <div className='adas-detail__report-card'>
      <div className='adas-detail__report-header'>
        <div className='adas-detail__report-header-left'>
          <h3 className='adas-detail__report-title'>ADAS Report</h3>
          <label className='adas-detail__collision-check'>
            <input type='checkbox' checked={!!collision} readOnly />
            <span>Vehicle was involved in {collisionPhrase}</span>
          </label>
        </div>
        <div className='adas-detail__report-header-right' style={{ position: 'relative' }} ref={menuRef}>
          <button className='adas-detail__btn-safety' onClick={() => setShowSafety(true)}>Safety Systems</button>
          <button className='adas-detail__btn-more' onClick={() => setShowMenu(v => !v)}>⋯</button>
          {showMenu && (
            <div className='adas-detail__more-menu' style={menuStyles.menu}>
              <button
                style={menuStyles.item}
                onClick={() => { setExpandAll(true); setShowMenu(false) }}
              >
                Expand All
              </button>
              <button
                style={menuStyles.item}
                onClick={() => { setExpandAll(false); setShowMenu(false) }}
              >
                Collapse All
              </button>
            </div>
          )}
        </div>
      </div>

      <div>
        {Object.entries(categories).map(([catName, sensors]) => (
          <div key={catName}>
            {Object.entries(sensors).map(([sensorName, { services: sensorServices }]) => (
              <div key={sensorName}>
                <div className='adas-detail__group-header'>
                  <span className='adas-detail__group-category'>{catName}</span>
                  <span className='adas-detail__group-divider'>|</span>
                  <span className='adas-detail__group-sensor'>{displaySensorName(sensorName)}</span>
                </div>

                {sensorServices.map(svc => {
                  const baseName = normalizeServiceDisplayName(svc.operation ? svc.operation.name : '')
                  const serviceName = baseName ? baseName + scanTypeSuffix(svc) : '—'
                  return (
                  <div key={svc._id} className='adas-detail__service-row'>
                    <div className='adas-detail__service-name'>
                      <span>{serviceName}</span>
                      {svc.calibrationType && (
                        <span className='calibration-type'>({svc.calibrationType})</span>
                      )}
                    </div>
                    <div className='adas-detail__service-status'>
                      <span className='adas-detail__meta-label'>Status:</span>
                      <StatusBadge status={svc.recommendedStatus} source={svc.recommendedSource} />
                    </div>
                    {allowViewPricing && (
                      <div className='adas-detail__service-price'>
                        <span className='adas-detail__meta-label'>Price:</span>
                        <span className='adas-detail__price-value'>{formatPrice(svc.amount)}</span>
                      </div>
                    )}
                    <span className='adas-detail__service-more'>⋯</span>
                  </div>
                  )
                })}

                <SensorInsights
                  sensorName={sensorName}
                  services={sensorServices}
                  sensorInsights={sensorInsights}
                  report={report}
                  collision={!!collision}
                  forceOpen={expandAll}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      <SafetySystemsModal show={showSafety} onHide={() => setShowSafety(false)} report={report} />
    </div>
  )
}

export default AdasSections
